/**
 * useOrbDocCoreConceptsTraitsENDocEx3TimingLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait OrbDocCoreConceptsTraitsENDocEx3Timing
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { emitCascadeEvent, useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, OrbDocCoreConceptsTraitsENStopwatch, OrbDocCoreConceptsTraitsENDocEx3TimingEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';

// ============================================================================
// State Machine
// ============================================================================

export type OrbDocCoreConceptsTraitsENDocEx3TimingState =
  | 'stopped'
  | 'running';

export type OrbDocCoreConceptsTraitsENDocEx3TimingEvent =
  | 'INIT'
  | 'START'
  | 'RESET'
  | 'STOP'
  | 'MOVED';

type OrbDocCoreConceptsTraitsENDocEx3TimingSlot = 'main';
interface OrbDocCoreConceptsTraitsENDocEx3TimingInternalState {
  machineState: OrbDocCoreConceptsTraitsENDocEx3TimingState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocCoreConceptsTraitsENDocEx3TimingEvent | null;
  slotWriter: Partial<Record<OrbDocCoreConceptsTraitsENDocEx3TimingSlot, OrbDocCoreConceptsTraitsENDocEx3TimingEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocCoreConceptsTraitsENDocEx3TimingEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocCoreConceptsTraitsENStopwatch>;
}

type OrbDocCoreConceptsTraitsENDocEx3TimingAction =
  | { type: 'EVENT_SUCCESS'; event: OrbDocCoreConceptsTraitsENDocEx3TimingEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: OrbDocCoreConceptsTraitsENDocEx3TimingSlot[]; renderClear?: OrbDocCoreConceptsTraitsENDocEx3TimingSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: OrbDocCoreConceptsTraitsENDocEx3TimingEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof OrbDocCoreConceptsTraitsENStopwatch & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function orbDocCoreConceptsTraitsENDocEx3TimingReducer(
  state: OrbDocCoreConceptsTraitsENDocEx3TimingInternalState,
  action: OrbDocCoreConceptsTraitsENDocEx3TimingAction
): OrbDocCoreConceptsTraitsENDocEx3TimingInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as OrbDocCoreConceptsTraitsENDocEx3TimingState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<OrbDocCoreConceptsTraitsENStopwatch>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<OrbDocCoreConceptsTraitsENStopwatch>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<OrbDocCoreConceptsTraitsENStopwatch>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: OrbDocCoreConceptsTraitsENDocEx3TimingInternalState = {
  machineState: 'stopped',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface OrbDocCoreConceptsTraitsENDocEx3TimingConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
}

export interface OrbDocCoreConceptsTraitsENDocEx3TimingLogicReturn {
  state: OrbDocCoreConceptsTraitsENDocEx3TimingState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocCoreConceptsTraitsENDocEx3TimingEvent | null;
  slotWriter: Partial<Record<OrbDocCoreConceptsTraitsENDocEx3TimingSlot, OrbDocCoreConceptsTraitsENDocEx3TimingEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocCoreConceptsTraitsENDocEx3TimingEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocCoreConceptsTraitsENStopwatch>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbDocCoreConceptsTraitsENDocEx3TimingEventPayloadMap]: (payload?: OrbDocCoreConceptsTraitsENDocEx3TimingEventPayloadMap[K]) => Promise<EventResponse> };
  config?: OrbDocCoreConceptsTraitsENDocEx3TimingConfig;
}

export function useOrbDocCoreConceptsTraitsENDocEx3TimingLogic(config?: Partial<OrbDocCoreConceptsTraitsENDocEx3TimingConfig>): OrbDocCoreConceptsTraitsENDocEx3TimingLogicReturn {
  const mergedConfig = config;

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/orb-doc-core-concepts-traits-en-doc-ex3-timing', orbitalName: 'OrbDocCoreConceptsTraitsEN', traitName: 'OrbDocCoreConceptsTraitsENDocEx3Timing' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(orbDocCoreConceptsTraitsENDocEx3TimingReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['OrbDocCoreConceptsTraitsENStopwatch'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'stopped' && event === 'INIT') {
      // stopped → stopped (on INIT)
      _newState = 'stopped';
    } else if (fromState === 'stopped' && event === 'START') {
      // stopped → running (on START)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocCoreConceptsTraitsENDocEx3Timing' }); emitCascadeEvent(eventBus, 'OrbDocCoreConceptsTraitsEN', 'OrbDocCoreConceptsTraitsENDocEx3Timing', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocCoreConceptsTraitsEN', trait: 'OrbDocCoreConceptsTraitsENDocEx3Timing' } }); })({ event: "START" as const }));
      _newState = 'running';
    } else if (fromState === 'stopped' && event === 'RESET') {
      // stopped → stopped (on RESET)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocCoreConceptsTraitsENDocEx3Timing' }); emitCascadeEvent(eventBus, 'OrbDocCoreConceptsTraitsEN', 'OrbDocCoreConceptsTraitsENDocEx3Timing', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocCoreConceptsTraitsEN', trait: 'OrbDocCoreConceptsTraitsENDocEx3Timing' } }); })({ event: "RESET" as const }));
        _fields['seconds'] = 0;
        fields = { ...(fields ?? {}), 'seconds': _fields['seconds'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'stopped';
    } else if (fromState === 'running' && event === 'INIT') {
      // running → stopped (on INIT)
      _newState = 'stopped';
    } else if (fromState === 'running' && event === 'STOP') {
      // running → stopped (on STOP)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocCoreConceptsTraitsENDocEx3Timing' }); emitCascadeEvent(eventBus, 'OrbDocCoreConceptsTraitsEN', 'OrbDocCoreConceptsTraitsENDocEx3Timing', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocCoreConceptsTraitsEN', trait: 'OrbDocCoreConceptsTraitsENDocEx3Timing' } }); })({ event: "STOP" as const }));
      _newState = 'stopped';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbDocCoreConceptsTraitsENDocEx3TimingSlot[]; clear: OrbDocCoreConceptsTraitsENDocEx3TimingSlot[] } => {
    switch (fromState) {
      case 'running':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocCoreConceptsTraitsENDocEx3TimingSlot[], clear: [] as OrbDocCoreConceptsTraitsENDocEx3TimingSlot[] };
          case 'STOP': return { write: ['main'] as OrbDocCoreConceptsTraitsENDocEx3TimingSlot[], clear: [] as OrbDocCoreConceptsTraitsENDocEx3TimingSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'stopped':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocCoreConceptsTraitsENDocEx3TimingSlot[], clear: [] as OrbDocCoreConceptsTraitsENDocEx3TimingSlot[] };
          case 'START': return { write: ['main'] as OrbDocCoreConceptsTraitsENDocEx3TimingSlot[], clear: [] as OrbDocCoreConceptsTraitsENDocEx3TimingSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'OrbDocCoreConceptsTraitsENStopwatch': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as OrbDocCoreConceptsTraitsENDocEx3TimingEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'OrbDocCoreConceptsTraitsENDocEx3Timing': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbDocCoreConceptsTraitsENDocEx3TimingEventPayloadMap]: (payload?: OrbDocCoreConceptsTraitsENDocEx3TimingEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'START': () => dispatch('START'),
    'RESET': () => dispatch('RESET'),
    'STOP': () => dispatch('STOP'),
    'MOVED': (payload?: OrbDocCoreConceptsTraitsENDocEx3TimingEventPayloadMap['MOVED']) => dispatch('MOVED', payload),
  };

  useUIEvents(enqueueEvent, 'OrbDocCoreConceptsTraitsEN.OrbDocCoreConceptsTraitsENDocEx3Timing', ['INIT', 'START', 'RESET', 'STOP'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('OrbDocCoreConceptsTraitsENDocEx3Timing', (): TraitStateSnapshot => ({
      traitName: 'OrbDocCoreConceptsTraitsENDocEx3Timing',
      currentState: internalStateRef.current.machineState,
      states: ['stopped', 'running'],
      events: ['INIT', 'START', 'RESET', 'STOP', 'MOVED'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refs for tick loops — avoid stale closures in rAF/interval callbacks
  const _tickStateRef = useRef(state);
  const _tickEntityRef = useRef<EntityRow>({ ...fields });
  _tickStateRef.current = state;
  _tickEntityRef.current = { ...fields };

  // Tick: second (every 1000ms)
  useEffect(() => {
    const interval = setInterval(() => {
      if ((_tickStateRef.current === "running")) {
        // Local entity copy for atomic tick computation
        const _ent: Record<string, EventPayloadValue | undefined> = { ..._tickEntityRef.current };
        _ent['seconds'] = (+(_ent?.seconds ?? 0) + 1);
        dispatchReducer({ type: 'TICK', collection: 'OrbDocCoreConceptsTraitsENStopwatch', updates: { 'seconds': _ent['seconds'] } });
        _tickEntityRef.current = _ent as EntityRow;
      }
    }, 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
